<template>
  <div class="mt-8" v-show="showDetails" test-id="biometric">
    <dispute-notification v-if="stageDispute">
      {{ stageDispute.comment }}
    </dispute-notification>

    <div
      class="border border-gray-30 rounded-lg p-4 flex flex-col gap-4 text-sm md:text-base"
      v-if="biometricStatus"
    >
      <div>
        <span class="text-gray-40">Name</span>
        <p class="mt-1">{{ kycInfo.accountName || '' }}</p>
      </div>
      <div class="flex justify-between items-end">
        <div>
          <span class="text-gray-40">Email</span>
          <p class="mt-1">{{ kycInfo.email || '' }}</p>
        </div>

        <div v-if="kycInfo.faceVerified" class="flex">
          <verified-icon fill="#2F855A" />
          <p class="italic text-foreground capitalize">
            {{ kycInfo.faceVerifiedStatus }}
          </p>
        </div>
      </div>
      <div>
        <span class="text-gray-40">Location</span>
        <p class="mt-1">{{ kycInfo.countryOfOperation || '' }}</p>
      </div>
    </div>

    <div class="flex flex-col gap-6" v-else>
      <div class="flex gap-5 md:gap-7 bg-gray-26 p-3">
        <span><alert-icon /></span>
        <ul class="list-disc">
          <li
            v-for="(item, index) in biometricInfo"
            :key="index"
            class="text-xs md:text-sm text-black-10 mb-1"
          >
            {{ item }}
          </li>
        </ul>
      </div>

      <el-form
        @keyup.enter.native="validateForm('kycDetailsForm')"
        @submit.native.prevent="validateForm('kycDetailsForm')"
        label-position="top"
        :model="kycDetailsForm"
        :rules="rules"
        ref="kycDetailsForm"
        test-id="form"
      >
        <el-form-item prop="email" class="relative">
          <p class="-mb-2">
            Email Address <span class="text-error-10">*</span>
          </p>
          <p class="text-gray-muted italic text-xs mb-3">
            The director with the highest shares or UBO (Ultimate Beneficial
            Owner)
          </p>
          <el-input
            v-model="kycDetailsForm.email"
            test-id="input"
            :readonly="isEmailSent"
          >
          </el-input>

          <p
            v-if="biometricEmail !== ''"
            class="italic text-sm md:text-base text-primary absolute right-5 bottom-3"
          >
            Email Sent!
          </p>
        </el-form-item>

        <button
          v-if="biometricEmail === ''"
          class="text-white bg-primary rounded-lg h-12 md:h-14 w-244px hover:opacity-90"
          type="submit"
          :disabled="isLoading || biometricStatus.status === $STATUS.APPROVED"
        >
          <spinner
            v-if="isLoading"
            size="small"
            :spacing="40"
            :line-fg-color="$primaryColor"
          />
          <span v-else>Send Verification Email</span>
        </button>

        <div v-else class="mt-2 flex gap-1 items-center text-sm text-gray-27">
          Didn't receive the email or want to restart?
          <p
            class="text-primary font-bold cursor-pointer hover:opacity-80"
            @click="clearBiometricEmail"
          >
            Click here.
          </p>
        </div>

        <p
          v-if="biometricEmail !== ''"
          class="mt-2 italic text-sm text-primary"
        >
          Note: Kindly login again 5 minutes after verification for changes to
          reflect
        </p>
      </el-form>
    </div>
  </div>
</template>

<script>
import kyc from '@/mixins/kyc.js'

export default {
  name: 'Biometric',
  mixins: [kyc],
  components: {
    AlertIcon: () => import('@/assets/v2/icons/InfoTwoIcon.vue'),
    VerifiedIcon: () => import('@/assets/v2/icons/CheckIcon.vue'),
    DisputeNotification: () =>
      import('@/components/v2/completeKycForm/common/DisputeNotification.vue')
  },
  props: {
    director: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      isLoading: false,
      kycDetailsForm: {
        email: ''
      },
      rules: {
        email: [
          {
            required: true,
            type: 'email',
            message: 'Please enter a valid email address',
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  computed: {
    kycStep () {
      return this.director.step
    },
    showDetails () {
      if (this.isBusinessTypeHNI) {
        return this.director.show && this.director.step === 3
      } else {
        return this.director.show && this.director.step === 6
      }
    },
    biometricEmail () {
      return this.$store.state.kyc.verificationEmail
    },
    kycInfo () {
      const contactType = this.isBusinessTypeHNI ? 'natural_person' : 'company'
      return (
        this.$store.state.kyc.kycDetails?.contacts.find(
          (item) => item.contactType === contactType
        ) || {}
      )
    },
    biometricStatus () {
      const biometricStep = this.stepStatus.find(
        (item) => item.stage === this.kycStep.toString()
      )
      return (
        biometricStep?.status === this.$STATUS.APPROVED ||
        biometricStep?.status === this.$STATUS.SUBMITTED ||
        false
      )
    },
    biometricInfo () {
      return [
        "Provide the director's email address with the highest shares or UBO (Ultimate Beneficial Owner).",
        'Click on the link that would be sent to the email you provided, and follow the steps provided by our verification partners.',
        'After the process is completed you can then login back in to complete your KYC process.'
      ]
    },
    isEmailSent () {
      return this.biometricEmail !== ''
    }
  },
  methods: {
    clearBiometricEmail () {
      this.$store.dispatch('kyc/setVerificationEmail', '')
      this.kycDetailsForm.email = ''
    },
    validateForm (kycDetailsForm) {
      this.$refs[kycDetailsForm].validate((valid) => {
        if (valid) {
          this.sendEmailVerification()
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    sendEmailVerification () {
      this.isLoading = true
      this.$store
        .dispatch('kyc/sendEmailVerification', this.kycDetailsForm.email)
        .then(() => {
          this.isLoading = false
          this.$store.dispatch(
            'kyc/setVerificationEmail',
            this.kycDetailsForm.email
          )
        })
        .catch((error) => {
          const err = error.response
            ? error?.response?.data?.error?.publicMessage
            : error
          if (error.response.data.code !== 401) {
            this.$notify({
              title: this.httpErrorMessage.customMessage,
              message: err || '',
              type: 'error'
            })
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
button:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
